/**
 * La largeur de l'activité au live, au grand écran, la visio ouverte : celle du volet (`pane`, le défaut)
 * ou agrandie (`enlarged`), choisie par le bouton rond sur la séparation (Wajih, daily du 23/10). Gardée
 * sur l'appareil par le même magasin que l'apparence de l'espace élève (`devicePreferenceStore`), sur sa
 * propre clé : l'élève la retrouve d'un live à l'autre sur l'ordinateur où il l'a choisie, et pas ailleurs.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from "react"

import { getFromStorage, setToStorage } from "../../../../data/utility/asyncStorageUtils"
import { type UILiveToggle } from "../types/studentLive"
import { createDevicePreferenceStore } from "./devicePreferenceStore"

export type LiveActivityWidth = "pane" | "enlarged"

/** Clé explicite, à côté de celle de l'apparence (`masteur.studentSpace.appearance`). */
export const LIVE_ACTIVITY_WIDTH_STORAGE_KEY = "masteur.studentSpace.liveActivityWidth"

/** Ce que le stockage rend : agrandie seulement si c'est dit ; tout le reste, le volet. */
export function toLiveActivityWidth(raw: unknown): LiveActivityWidth {
    return raw === "enlarged" ? "enlarged" : "pane"
}

const store = createDevicePreferenceStore<LiveActivityWidth>(
    { read: getFromStorage, write: setToStorage },
    { storageKey: LIVE_ACTIVITY_WIDTH_STORAGE_KEY, fallback: "pane", parse: toLiveActivityWidth }
)

/**
 * L'activité est-elle agrandie, et le geste qui la bascule ; la lecture de l'appareil part au premier
 * lecteur, une fois. `undefined` tant qu'elle n'a pas abouti : la page n'offre pas encore le bouton, et pose
 * la largeur lue sans la faire glisser (une largeur gardée qui glisserait à chaque entrée dans le live).
 */
export function useLiveActivityEnlarged(): UILiveToggle | undefined {
    const width = useSyncExternalStore(store.subscribe, store.get, store.get)
    // État d'interface : la lecture de l'appareil a abouti — d'emblée, d'un live à l'autre.
    const [read, setRead] = useState(store.hydrated)

    useEffect(() => {
        let alive = true
        void store.hydrate().then(() => {
            if (alive) setRead(true)
        })
        return () => {
            alive = false
        }
    }, [])

    const onToggle = useCallback(() => store.set(store.get() === "enlarged" ? "pane" : "enlarged"), [])
    return read ? { on: width === "enlarged", onToggle } : undefined
}
